<template>
    <label
        :class="{
            'border-red-600': error,
        }"
        class="group relative block rounded-8 border border-ash-45 p-3 pt-4 transition-colors duration-300 has-[:disabled]:border-ash-300">
        <input
            v-model="model"
            placeholder=" "
            v-bind="$attrs"
            :min="min"
            :max="max || undefined"
            :disabled="disabled"
            :type="type"
            :inputmode="inputMode"
            :class="
                type === 'number'
                    ? '[-moz-appearance:textfield] [appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none'
                    : ''
            "
            @blur="handleBlur"
            @keydown.enter="handleEnter"
            class="input peer relative w-full bg-transparent font-semibold text-white outline-none transition-colors duration-300 disabled:text-white/50" />
        <span
            class="pointer-events-none absolute left-3 top-0 select-none text-16/148 text-ash-300 transition-all duration-300 ease-in-out peer-placeholder-shown:top-3.5 peer-focus:top-0 peer-focus:text-14/148">
            {{ placeholder }}
        </span>
        <div
            v-if="type === 'number'"
            class="absolute right-0 top-0 flex h-full flex-col justify-around divide-y divide-ash-45 rounded-r-4 border-l border-ash-45 peer-disabled:divide-ash-300 peer-disabled:border-ash-300 peer-disabled:text-ash-300">
            <button
                aria-label="увеличить"
                :disabled="disabled"
                class="p-1"
                @click="increaseNumber">
                <AtomIcon
                    name="chevron-down"
                    :class="
                        Number(model) === Number(max)
                            ? 'text-ash-300'
                            : 'active:-translate-y-px'
                    "
                    class="size-4 rotate-180 transition-all duration-150" />
            </button>
            <button
                aria-label="уменьшить"
                :disabled="disabled"
                class="p-1"
                @click="decreaseNumber">
                <AtomIcon
                    name="chevron-down"
                    :class="
                        Number(model) === Number(min)
                            ? 'text-ash-300'
                            : 'active:translate-y-px'
                    "
                    class="size-4 transition-all duration-150" />
            </button>
        </div>
        <Transition name="error-fade">
            <div
                v-if="error"
                class="absolute -bottom-2 right-3 z-10 rounded-4 bg-red-600 px-1 text-12/140 text-white">
                {{ error }}
            </div>
        </Transition>
    </label>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'

const model = defineModel({
    type: [String, Number] as PropType<string | number>,
    required: true,
})

const props = defineProps({
    placeholder: {
        type: String as PropType<string>,
        default: '',
    },
    disabled: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
    type: {
        type: String as PropType<'tel' | 'email' | 'text' | 'number'>,
        default: 'text',
    },
    inputMode: {
        type: String as PropType<
            'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'
        >,
        default: 'text',
    },
    min: {
        type: [String, Number] as PropType<string | number>,
        default: 1,
    },
    max: {
        type: [String, Number, null] as PropType<string | number | null>,
        default: null,
    },
    deferNumberValidation: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
    error: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
})

const emits = defineEmits(['change', 'blur', 'keydown'])

function normalizeNumberValue(value: string | number): number {
    const parsedValue =
        typeof value === 'number' ? value : Number.parseInt(value, 10)
    const minValue = Number(props.min)
    const maxValue = Number(props.max)

    let normalizedValue = Number.isNaN(parsedValue) ? minValue : parsedValue

    if (normalizedValue < minValue) {
        normalizedValue = minValue
    } else if (
        props.max !== null &&
        !Number.isNaN(maxValue) &&
        normalizedValue > maxValue
    ) {
        normalizedValue = maxValue
    }

    if (!Number.isInteger(normalizedValue)) {
        normalizedValue = Math.round(normalizedValue)
    }

    return normalizedValue
}

function commitNumberValue(): void {
    if (props.type !== 'number') return

    model.value = normalizeNumberValue(model.value)
    emits('change', model.value)
}

function handleBlur(event: FocusEvent): void {
    if (props.deferNumberValidation) {
        commitNumberValue()
    }

    emits('blur', event)
}

function handleEnter(event: KeyboardEvent): void {
    if (props.deferNumberValidation) {
        commitNumberValue()
    }

    emits('keydown', event)
}

function increaseNumber(): void {
    if (props.type !== 'number') return

    model.value = normalizeNumberValue(Number(model.value) + 1)
    emits('change', model.value)
}

function decreaseNumber(): void {
    if (props.type !== 'number') return

    model.value = normalizeNumberValue(Number(model.value) - 1)
    emits('change', model.value)
}

watch(model, (): void => {
    if (props.type !== 'number') return
    if (props.deferNumberValidation) return

    model.value = normalizeNumberValue(model.value)
    emits('change', model.value)
})
</script>
